import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  TextField,
  DialogActions,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiInput from "components/SuiInput";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import PDFApi from "api/pdfApi";
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const DesignarProcesoModal = ({ open, onClose, procesoIdentificador, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [proceso, setProceso] = useState(null);
  const [operadores, setOperadores] = useState([]);
  const [nuevoResponsable, setNuevoResponsable] = useState(null);
  const [obserDesignacion, setObserDesignacion] = useState("");
  const [fecha, setFecha] = useState("");
  const [loading, setLoading] = useState(false);
  const FILE_NAME = "pdf_designacion.pdf";
  const ANEXO_TYPE = "PDF_DESIGNACION";

  const fetchInitialData = async () => {
    try {
      const [procesoResp, responsablesRes] = await Promise.all([
        ProcesosApi.fetchProcesoByIdentificador(procesoIdentificador),
        ProcesosApi.fetchResponsables({ roles: ["OPERADOR(A)"] }),
      ]);
      setProceso(procesoResp.data);
      setOperadores(responsablesRes.data || []);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos iniciales."));
    }
  };

  useEffect(() => {
    if (procesoIdentificador) {
      fetchInitialData();
    }
  }, [procesoIdentificador]);

  useEffect(() => {
    const fechaActual = new Date().toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    setFecha(fechaActual);
  }, [open]);

  const hCNuevoResponsable = (value) => {
    setNuevoResponsable(value);
  };

  const hcObserAceptacion = (event) => {
    setObserDesignacion(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nuevoResponsable) {
      setError("Debes seleccionar un Operador del Proceso.");
      return;
    }
    if (obserDesignacion === "") {
      setError("Debes registrar una Observación.");
      return;
    }
    const datos = {
      operacion: "DESIGNAR_PROCESO",
      id_proceso: proceso.id,
      responsable: nuevoResponsable.id,
      obser_designacion: obserDesignacion,
    };
    //console.log("DESIGNAR_PROCESO.datos: " + JSON.stringify(datos));
    const confirmAction = window.confirm(
      `¿Está seguro(a) de designar el Proceso ${proceso.identificador}?`
    );
    if (!confirmAction) return;
    try {
      setLoading(true);
      const response = await ProcesosApi.operarProceso(datos);
      if (response.status === 201 && response.data) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(proceso.id, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(proceso.id, anexo.id);
        }
        const pdfResponse = await PDFApi.generateDocument("designacionProceso", proceso.id);
        const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
        const formData = new FormData();
        formData.append("file", pdfBlob, FILE_NAME);
        formData.append("file_type", "proceso");
        formData.append("proceso_id", proceso.identificador);
        formData.append("registrar_anexo", "true");
        formData.append("tipo_anexo", ANEXO_TYPE);
        formData.append("nombre_anexo", "Designación Proceso");
        await BucketApi.uploadFile(formData);
        alert("Operación realizada con éxito");
        setError(undefined);
        setObserDesignacion("");
        onSuccess();
      }
      onSuccess();
    } catch (error) {
      console.error(`Error al designar el Proceso:`, error);
      setError(`Error al designar el Proceso.`);
    } finally {
      setLoading(false);
    }
  };

  const hCClose = () => {
    setError(undefined);
    setObserDesignacion("");
    onClose();
  };

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <h2>DESIGNACIÓN PROCESO: {proceso ? proceso.identificador : "No tiene Proceso"}</h2>
          <h3>Debe seleccinar el Operador al cual se le asignará el Proceso</h3>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>Operador(a) *</Grid>
              <Grid size={8}>
                <SuiBox mb={2}>
                  <Autocomplete
                    disablePortal
                    options={operadores}
                    getOptionLabel={(option) => option?.nombre_completo || ""}
                    sx={{ mb: 3 }}
                    renderInput={(params) => <TextField {...params} />}
                    value={nuevoResponsable || null}
                    onChange={(_, newValue) => hCNuevoResponsable(newValue)}
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>Obser. Designación *</Grid>
              <Grid size={8}>
                <SuiBox mb={2}>
                  <SuiInput
                    name="obserDesignacion"
                    placeholder="Ingrese observación de designación"
                    multiline
                    rows={4}
                    value={obserDesignacion || ""}
                    onChange={hcObserAceptacion}
                    fullWidth
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>Fecha Designación *</Grid>
              <Grid size={8}>
                <SuiInput
                  name="fecha"
                  label="Fecha de Aceptación"
                  value={fecha}
                  fullWidth
                  disabled
                  readOnly
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <SuiButton onClick={hCClose} buttonColor="light">
              Cancelar
            </SuiButton>
            <SuiButton type="submit" variant="gradient" buttonColor="dark">
              Aceptar
            </SuiButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

DesignarProcesoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procesoIdentificador: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default DesignarProcesoModal;
